/* eslint-disable react-hooks/exhaustive-deps */
/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */

//Standard libraries
import React, { Dispatch, Suspense, useCallback } from "react";
import { BrowserRouter, useLocation, useRoutes } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import Axios, { Canceler } from "axios";
import getDisplayName from "react-display-name";
//Foundation libraries
import { site } from "./_foundation/constants/site";
import { initAxios } from "./_foundation/axios/axiosConfig";
import { initSite, useSite } from "./_foundation/hooks/useSite";
import LoginGuard from "./_foundation/guard/LoginGuard";
import { storageSessionHandler } from "./_foundation/utils/storageUtil";
import storeService from "./_foundation/apis/transaction/store.service";
import { PRODUCTION } from "./_foundation/constants/common";
import shippingInfoService from "./_foundation/apis/transaction/shippingInfo.service";
import Swiper from "swiper";
//Custom libraries
import { ROUTE_CONFIG } from "./configs/routes";
import { DISCOVER_FEATURE, EMPTY_STRING } from "./constants/common";
import { Header } from "./components/header";
import { Footer } from "./components/footer";
import { Extensions } from "./components/extensions";
import { useCSRForUser } from "./_foundation/hooks/useCSRForUser";
import SuccessMessageSnackbar from "./components/widgets/message-snackbar/SuccessMessageSnackbar";
import ErrorMessageSnackbar from "./components/widgets/message-snackbar/ErrorMessageSnackbar";
import { IFRAME_RESIZER } from "./_foundation/constants/csr";
//Redux and context
import { forUserIdSelector, loginStatusSelector } from "./redux/selectors/user";
import { INIT_STATE_FROM_STORAGE_ACTION, LISTEN_USER_FROM_STORAGE_ACTION } from "./redux/actions/user";
import { GET_COUNTRY_STATE_LIST_ACTION } from "./redux/actions/country";
import { useStoreShippingModeValue } from "./_foundation/context/store-shipping-mode-context";
//UI
import { StyledWrapper } from "./components/StyledUI";
import { StyledGrid, StyledProgressPlaceholder } from "@hcl-commerce-store-sdk/react-component";
import "./App.scss";
//Custom Styles
import "./components/commerce-layouts/product-listing-page/home-page-slider.scss";
import "./components/commerce-layouts/product-listing-page/home-page-slider-aux.scss";
//GA360
//UA
import GTMDLService from "./_foundation/gtm/ua/gtmDataLayer.service";
//GA4
import GA4GTMDLService from "./_foundation/gtm/ga4/gtmDataLayer.service";
import { PreviewNonManagedLayout } from "./_foundation/preview/layout/preview-non-managed-layout";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    //scroll to top on path change.
    setTimeout(() => {
      window["topOrSelf"] && window["topOrSelf"].scrollTo(0, 0);
    });
  }, [pathname]);
  return null;
};

const RouteRenderer = () => {
  const { mySite } = useSite();
  const e = useRoutes(mySite.isB2B ? ROUTE_CONFIG.B2B : ROUTE_CONFIG.B2C);
  return e;
};

const App: React.FC = (props: any) => {
  const widgetName = getDisplayName(App);
  const loggedIn = useSelector(loginStatusSelector);
  const forUserId = useSelector(forUserIdSelector);
  const dispatch = useDispatch<Dispatch<any>>();
  const { mySite, storeDisplayName } = useSite();
  const { receiveParentMessage } = useCSRForUser();
  const CancelToken = Axios.CancelToken;
  const MemRouteRenderer = useCallback(RouteRenderer, []);
  const { setStoreShippingMode } = useStoreShippingModeValue();

  const cancels: Canceler[] = [];
  const payloadBase: any = {
    widget: widgetName,
    cancelToken: new CancelToken(function executor(c) {
      cancels.push(c);
    }),
  };

  const [discover, setDiscover] = React.useState<boolean>(false);
  const [pageLoaded, setPageLoaded] = React.useState(window.location.pathname);

  initAxios(dispatch);

  /**
   * Function to check Discover is enabled for store based on storeId
   *
   * @param storeID
   */
  const isDiscoverEnabled = (storeID: string) => {
    const payload = {
      storeId: storeID,
      ...payloadBase,
    };
    storeService
      .getStoreEnabledFeaturesList(payload)
      .then((res) => {
        if (res.data && res.data.resultList) {
          setDiscover(res.data.resultList.includes(DISCOVER_FEATURE));
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  function hashHandler() {
    let oldHash = "";
    if (pageLoaded != "/") {
      oldHash = pageLoaded.split("/")[pageLoaded.split("/").length - 1];
      oldHash = oldHash === "" || oldHash === "home" ? "/" : oldHash;
    } else {
      oldHash = pageLoaded;
    }
    setInterval(detect, 500);

    function detect() {
      if (oldHash != window.location.pathname) {
        oldHash = window.location.pathname;
        if (oldHash != "/") {
          const path = oldHash.split("/")[oldHash.split("/").length - 1];
          setPageLoaded(path);
        } else {
          setPageLoaded(window.location.pathname);
        }
      }
    }
  }

  React.useEffect(() => {
    hashHandler();
  }, [pageLoaded]);

  React.useEffect(() => {
    if (mySite) {
      dispatch(INIT_STATE_FROM_STORAGE_ACTION({ ...payloadBase }));
      dispatch(GET_COUNTRY_STATE_LIST_ACTION({ ...payloadBase }));
      storageSessionHandler.triggerUserStorageListener(() =>
        dispatch(LISTEN_USER_FROM_STORAGE_ACTION({ ...payloadBase }))
      );
      isDiscoverEnabled(mySite.storeID);
      shippingInfoService
        .getAllowableShippingModes(mySite.storeID, undefined, undefined, undefined, { widget: widgetName })
        .then((res) => {
          setStoreShippingMode(res.data?.usableShippingMode ?? []);
        })
        .catch((e) => console.log("fail to get store shipping mode", e));
      //GA360
      if (mySite.enableGA) {
        if (mySite.enableUA) {
          GTMDLService.initializeGTM(mySite.gtmID, mySite.gtmAuth, mySite.gtmPreview);
        }
        if (mySite.enableGA4 && !mySite.enableUA) {
          GA4GTMDLService.initializeGTM(mySite.gtmID, mySite.gtmAuth, mySite.gtmPreview);
        }
      }
    } else {
      initSite(site, dispatch);
    }
  }, [mySite, dispatch]);

  React.useEffect(() => {
    if (forUserId) {
      window[IFRAME_RESIZER] = {
        onMessage: receiveParentMessage,
      };
    } else {
      window[IFRAME_RESIZER] = undefined;
    }
  }, [forUserId]);

  React.useEffect(() => {
    return () => {
      cancels.forEach((cancel) => cancel());
    };
  }, []);
  
  const baseName = process.env.REACT_APP_ROUTER_BASENAME ? { basename: process.env.REACT_APP_ROUTER_BASENAME } : {};

  // public url path for accessing discoverui.js file.
  const publicUrlPath = process.env.PUBLIC_URL ? process.env.PUBLIC_URL : EMPTY_STRING;

  return mySite ? (
    <>
      <BrowserRouter {...baseName}>
        <StyledWrapper data-testid="app-wrapper">
          <SuccessMessageSnackbar />
          <ErrorMessageSnackbar />
          <StyledGrid
            container
            direction="column"
            justifyContent="space-evenly"
            alignItems="stretch"
            className="full-viewport-height">
            <StyledGrid item xs={false}>
              <Header loggedIn={loggedIn} />
              <LoginGuard />
              <PreviewNonManagedLayout />
              <ScrollToTop />
              <Helmet>
                <meta charSet="utf-8" />
                <title>{`${storeDisplayName}`}</title>
                {discover && (
                  <script src={`${publicUrlPath}/discover/discoverui.js?q=${Date.now()}`} type="text/javascript" async />
                )}
                {window[IFRAME_RESIZER] && (
                  <script src="/iframeResizer.contentWindow.min.js" type="text/javascript" async />
                )}
              </Helmet>
            </StyledGrid>

            <StyledGrid item xs className="full-width">
              <Suspense fallback={<StyledProgressPlaceholder className="vertical-padding-20" />}>
                <MemRouteRenderer />
              </Suspense>
            </StyledGrid>

            <StyledGrid item xs={false}>
              <Footer />
            </StyledGrid>
            {process.env.NODE_ENV !== PRODUCTION && <Extensions />}
          </StyledGrid>
        </StyledWrapper>
      </BrowserRouter>
      {pageLoaded === "/" || pageLoaded === "" ? (
        <Helmet>
        <script type="text/javascript">
          {`
          console.log(${pageLoaded});
          var home = document.getElementById("promo_slider_desktop");
          var brand = document.getElementById("slider_brands_content");

          comprueba();

          async function comprueba() {
            do {
              await timeout(1000);
              await leerContenedor();
              console.log(home);
              console.log(brand);
            } while ( home == null || brand == null );
          };

          function timeout(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
          }

          function leerContenedor() {
            home = document.getElementById("promo_slider_desktop");
            brand = document.getElementById("slider_brands_content");
            if( home != null ) {
              setLinksDesktopPromotional();
              setLinksMobilePromotional();
              setLinksBrands();
              //createLinks();
              //createCatalogLinkHome();
              //createAppLinksHome();
              //addLinksCatalogStores();
              //createLinksBottomCards();
            }

            if(brand != null) {
              setLinksBrands();
            }

            return false;
          };


          function moveToCatalogPage(){
            let links = Array.from(document.getElementsByClassName("btn-link-catalogo"));
            let elements = document.querySelectorAll(".btn-link-catalogo");
            links.map((element,index) => {
              let idbtn = element.getAttribute("id");
              let url = element.getAttribute("data-url");
              let a_tag = document.createElement("a");
              a_tag.setAttribute('target',"_blank");
              a_tag.setAttribute('class',"btn-link-catalogo-1");
              a_tag.href = url;
              element.appendChild(a_tag);
            });
          }
          

          function closeModal(){
            let modal = document.getElementById("content-tarjeta");
            modal.style.display = "none";
          }

          function setLinksDesktopPromotional() {
            let promotionals = Array.from(document.getElementsByClassName("promo-slider"));
            let psd = document.getElementById("promo_slider_desktop");
            let div_swiper = document.createElement("div");
            let div_wrapper = document.createElement("div");
            let div_pagination = document.createElement("div");

            psd.innerHTML = "";
            div_swiper.className = "swiper mySwiper";
            div_wrapper.id = "desktop_promotional_swiper_wrapper";
            div_wrapper.className = "swiper-wrapper";
            div_pagination.className = "swiper-pagination";

            promotionals.map((element, index) => {
              element.style.display = "block";
              let div_slider = document.createElement("div");
              let img_link = document.createElement("a");
              let url_a = element.getAttribute("data-url");

              div_slider.className = "swiper-slide";
              img_link.href = url_a;
              img_link.appendChild(element);
              div_slider.appendChild(img_link);
              div_wrapper.appendChild(div_slider);
            });

            div_swiper.appendChild(div_wrapper);
            div_swiper.appendChild(div_pagination);
            psd.appendChild(div_swiper);

            sliderDesktopPromotional();
          }

          function setLinksMobilePromotional() {
            let mobile_promotionals = Array.from(document.getElementsByClassName("promo-slider-mobile"));
            let psm = document.getElementById("promo_slider_mobile");
            let div_swiper_m = document.createElement("div");
            let div_wrapper_m = document.createElement("div");
            let div_pagination_m = document.createElement("div");

            psm.innerHTML = "";
            div_swiper_m.className = "swiper promotional_mobile";
            div_wrapper_m.id = "mobile_promotional_swiper_wrapper";
            div_wrapper_m.className = "swiper-wrapper";
            div_pagination_m.className = "swiper-pagination";

            mobile_promotionals.map((element_m, index_m) => {
              element_m.style.display = "block"
              let div_slider_m = document.createElement("div");
              let img_link_m = document.createElement("a");
              let url_a_m = element_m.getAttribute("data-url");

              div_slider_m.className = "swiper-slide";
              img_link_m.href = url_a_m;
              img_link_m.appendChild(element_m);
              div_slider_m.appendChild(img_link_m);
              div_wrapper_m.appendChild(div_slider_m);
            });

            div_swiper_m.appendChild(div_wrapper_m);
            div_swiper_m.appendChild(div_pagination_m);
            psm.appendChild(div_swiper_m);

            sliderMobilePromotional();
          }

          
          function setLinksBrands() {
            let brandsElements = Array.from(document.getElementsByClassName("img100"));
            let bs = document.getElementById("slider_brands_content");
            let div_swiper = document.createElement("div");
            let div_wrapper = document.createElement("div");
            
            bs.innerHTML = "";
            div_swiper.className = "swiper swiper-container-brands";
            div_wrapper.id = "brands_swiper_wrapper";
            div_wrapper.className = "swiper-wrapper";
            
            brandsElements.map((element, index) => {
              let div_slider = document.createElement("div");
              let brand_link = document.createElement("a");
              let url_brand = element.getAttribute("data-url");
              
              div_slider.className = "swiper-slide";
              brand_link.href = url_brand;
              brand_link.appendChild(element);
              div_slider.appendChild(brand_link);
              div_wrapper.appendChild(div_slider);
            });
            
            div_swiper.appendChild(div_wrapper);
            bs.appendChild(div_swiper);

            sliderBrands();
          }

          function sliderDesktopPromotional() {
            var swiper = new Swiper(".mySwiper", {
                slidesPerView: 1,
                spaceBetween: 0,
                loop: true,
                autoplay: {
                  delay: 10000,
                  disableOnInteraction: false,
                },
                pagination: {
                  el: ".swiper-pagination",
                  clickable: true,
                },
                navigation: {
                  nextEl: ".swiper-button-next",
                  prevEl: ".swiper-button-prev",
                }
            });
          }

          function sliderMobilePromotional() {
            var swiper_mobile = new Swiper(".promotional_mobile", {
                slidesPerView: 1,
                spaceBetween: 0,
                loop: true,
                autoplay: {
                  delay: 10000,
                  disableOnInteraction: false,
                },
                pagination: {
                  el: ".swiper-pagination",
                  clickable: true,
                },
                navigation: {
                  nextEl: ".swiper-button-next",
                  prevEl: ".swiper-button-prev",
                }
            });
          }
          

          function sliderBrands() {
            var swiper_brands = new Swiper('.swiper-container-brands', {
              slidesPerView: 5,
              spaceBetween: 10,
              loop: true,
              autoplay: {
                delay: 2500,
                disableOnInteraction: false
              },
              breakpoints: {
                320: {
                  slidesPerView: 3,
                  spaceBetween: 10
                },
                640: {
                  slidesPerView: 5,
                  spaceBetween: 10
                },
                768: {
                  slidesPerView: 8,
                  spaceBetween: 10
                },
                1024: {
                  slidesPerView: 11,
                  spaceBetween: 10
                }
              }
            });
          }`}
          </script>
        </Helmet>
      ):(<></>)}
    </>
  ) : null;
};

export default App;
